import AsyncData from '@sysarb/sysarb-api/models/common/AsyncData';
import type Mapping from '@sysarb/sysarb-api/models/entities/Mapping';
import type MappingEmployment from '@sysarb/sysarb-api/models/entities/MappingEmployment';
import type MappingFactor from '@sysarb/sysarb-api/models/entities/MappingFactor';
import type MappingFactorValue from '@sysarb/sysarb-api/models/entities/MappingFactorValue';
import type MappingWorkEqualWorksTag from '@sysarb/sysarb-api/models/entities/MappingWorkEqualWorksTag';
import type MappingWorkConnectedWorksTag from '@sysarb/sysarb-api/models/entities/MappingWorkConnectedWorksTag';
import type MappingWorkConnectedWorksComment from '@sysarb/sysarb-api/models/entities/MappingWorkConnectedWorksComment';
import type { Guid } from 'guid-typescript';
import { defineStore } from 'pinia';
import type MappingWork from '@sysarb/sysarb-api/models/entities/MappingWork';
import type MappingWorkValuation from '@sysarb/sysarb-api/models/entities/MappingWorkValuation';
import type MappingEmploymentTag from '@sysarb/sysarb-api/models/entities/MappingEmploymentTag';
import type MappingEmploymentComment from '@sysarb/sysarb-api/models/entities/MappingEmploymentComment';
import type MappingEmploymentAction from '@sysarb/sysarb-api/models/entities/MappingEmploymentAction';

interface PayAuditEntityState {
    _mapping: AsyncData<Mapping>;
    _mappingEmployment: AsyncData<MappingEmployment[]>;
    _mappingWorkEqualWorksTag: AsyncData<MappingWorkEqualWorksTag[]>;
    _mappingWorkConnectedWorksTag: AsyncData<MappingWorkConnectedWorksTag[]>;
    _mappingWorkConnectedWorksComment: AsyncData<MappingWorkConnectedWorksComment[]>;
    _mappingWork: AsyncData<MappingWork[]>;
    _mappingWorkValuation: AsyncData<MappingWorkValuation[]>;
    _mappingEmploymentTag: AsyncData<MappingEmploymentTag[]>;
    _mappingFactor: AsyncData<MappingFactor[]>;
    _mappingFactorValue: AsyncData<MappingFactorValue[]>;
    _mappingEmploymentComment: AsyncData<MappingEmploymentComment[]>;
    _mappingEmploymentAction: AsyncData<MappingEmploymentAction[]>;
}

interface IEntityDataStatePayload {
    id?: string,
    field?: string,
    value?: any,
    promise?: Promise<any>
}

export const usePayAuditEntityStore = defineStore('payAuditEntity', {
    state: (): PayAuditEntityState => ({
        _mapping: new AsyncData<Mapping>(),
        _mappingEmployment: new AsyncData<MappingEmployment[]>([]),
        _mappingWorkConnectedWorksTag: new AsyncData<MappingWorkConnectedWorksTag[]>([]),
        _mappingWorkEqualWorksTag: new AsyncData<MappingWorkEqualWorksTag[]>([]),
        _mappingWorkConnectedWorksComment: new AsyncData<MappingWorkConnectedWorksComment[]>([]),
        _mappingWorkValuation: new AsyncData<MappingWorkValuation[]>([]),
        _mappingEmploymentTag: new AsyncData<MappingEmploymentTag[]>([]),
        _mappingWork: new AsyncData<MappingWork[]>([]),
        _mappingFactor: new AsyncData<MappingFactor[]>([]),
        _mappingFactorValue: new AsyncData<MappingFactorValue[]>([]),
        _mappingEmploymentComment: new AsyncData<MappingEmploymentComment[]>([]),
        _mappingEmploymentAction: new AsyncData<MappingEmploymentAction[]>([]),
    }),
    getters: {
        mappingIsLoading: state => state._mapping.isLoading,
        mappingWorkEqualWorksTags: state => state._mappingWorkEqualWorksTag.data,
        mappingEmploymentTags: state => state._mappingEmploymentTag.data,
        mappingWorks: state => state._mappingWork.data,
        mappingEmployments: state => state._mappingEmployment.data,
        mappingEmploymentAction: state => state._mappingEmploymentAction.data,
        mappingFactorsByID: state => (mappingFactorID: Guid | null): any => (state._mappingFactor == null ? null : state._mappingFactor.data.find(x => x.id?.toString() === mappingFactorID?.toString())),
        mappingFactorValuesByMappingFactorID: state => (mappingFactorID: Guid | null): any => (state._mappingFactorValue?.isLoading ? null : state._mappingFactorValue.data.filter(x => x.mappingFactorID.id.toString() === mappingFactorID?.toString()))
    },
    actions: {
        setEntityStoreData(storePropertyToUpdate: string, payload: IEntityDataStatePayload) {
            this[storePropertyToUpdate as keyof PayAuditEntityState] = payload.value;
        },
        addEntityStoreData(storePropertyToUpdate: string, payload: IEntityDataStatePayload) {
            const storePropertyData = this[storePropertyToUpdate as keyof PayAuditEntityState];
            if (storePropertyData == null) throw new Error('Store property doesnt exist');
            if (!Array.isArray(storePropertyData.data)) return;
            if (!storePropertyData.data.some((x: any) => x.id?.toString() === payload.value.id.toString())) {
                storePropertyData.data.push(payload.value);
            }
        },
        deleteEntityStoreData(storePropertyToUpdate: string, payload: IEntityDataStatePayload) {
            const storePropertyData = this[storePropertyToUpdate as keyof PayAuditEntityState];
            if (storePropertyData == null) throw new Error('Store property doesnt exist');
            if (Array.isArray(storePropertyData.data)) {
                const entityToDelete = (storePropertyData.data as []).find((x: any) => x.id?.isEqualTo(payload.id) === true);
                const ids = storePropertyData.data.map(x => x.id?.toString());
                const index = ids.indexOf(payload.id?.toString());
                if (entityToDelete != null) {
                    storePropertyData.data.splice(index, 1);
                    payload.promise?.catch((err: unknown) => {
                        if (!Array.isArray(storePropertyData.data)) throw err;
                        // reset the value & throw the error
                        (storePropertyData.data as []).splice(index, 1);
                        throw err;
                    });
                }
            } else {
                storePropertyData.clear();
            }
        },
        updateEntityStoreData(storePropertyToUpdate: string, payload: IEntityDataStatePayload) {
            if (payload.field == null) throw new Error('No field provided');
            const storePropertyData = this[storePropertyToUpdate as keyof PayAuditEntityState];
            if (storePropertyData == null) throw new Error('Store property doesnt exist');
            if (Array.isArray(storePropertyData.data)) {
                const entityToUpdate = (storePropertyData.data as []).find((x: any) => x.id?.toString() === payload.id);
                const ids = storePropertyData.data.map(x => x.id?.toString());
                const index = ids.indexOf(payload.id?.toString());
                if (entityToUpdate != null && payload.field != null) {
                    const previousValue = (entityToUpdate as any)[payload.field];
                    (storePropertyData.data[index] as any)[payload.field] = payload.value;
                    const newValue: any = storePropertyData.data[index];
                    // This is done to make reactivity
                    storePropertyData.data.splice(index, 1, newValue);

                    payload.promise?.catch((err: unknown) => {
                        if (!Array.isArray(storePropertyData.data)) throw err;
                        // reset the value & throw the error
                        storePropertyData.data.splice(index, 1, previousValue);
                        throw err;
                    });
                }
            } else if (storePropertyData.data.id?.toString() === payload.id) {
                const previousValue = (storePropertyData.data as any)[payload.field];
                (storePropertyData.data as any)[payload.field] = payload.value;
                payload.promise?.catch((err: unknown) => {
                    if (payload.field == null) throw new Error('No field provided');
                    // reset the value & throw the error
                    (storePropertyData.data as any)[payload.field] = previousValue;
                    throw err;
                });
            }
        },
        addMappingWorkEqualWorksTag(mappingWorkEqualWorksTag: MappingWorkEqualWorksTag) {
            const alreadyExists = this._mappingWorkEqualWorksTag.data.some((x: any) => x.id.isEqualTo(mappingWorkEqualWorksTag.id) === true);
            if (!alreadyExists) {
                this._mappingWorkEqualWorksTag.data.push(mappingWorkEqualWorksTag);
            }
        },
        addMappingWorkConnectedWorksTag(mappingWorkConnectedWorksTag: MappingWorkConnectedWorksTag) {
            const alreadyExists = this._mappingWorkConnectedWorksTag.data.some((x: any) => x.id.isEqualTo(mappingWorkConnectedWorksTag.id) === true);
            if (!alreadyExists) {
                this._mappingWorkConnectedWorksTag.data.push(mappingWorkConnectedWorksTag);
            }
        },
        addMappingEmploymentTag(mappingEmploymentTag: MappingEmploymentTag) {
            const alreadyExists = this._mappingEmploymentTag.data.some((x: any) => x.id.isEqualTo(mappingEmploymentTag.id) === true);
            if (!alreadyExists) {
                this._mappingEmploymentTag.data.push(mappingEmploymentTag);
            }
        },
        addMappingWorkConnectedWorksComment(mappingWorkConnectedWorksComment: MappingWorkConnectedWorksComment) {
            const alreadyExists = this._mappingWorkConnectedWorksComment.data.some((x: any) => x.id.isEqualTo(mappingWorkConnectedWorksComment.id) === true);
            if (!alreadyExists) {
                this._mappingWorkConnectedWorksComment.data.push(mappingWorkConnectedWorksComment);
            }
        },
        addMappingEmploymentComment(mappingEmploymentComment: MappingEmploymentComment) {
            const alreadyExists = this._mappingEmploymentComment.data.some((x: any) => x.id.isEqualTo(mappingEmploymentComment.id) === true);
            if (!alreadyExists) {
                this._mappingEmploymentComment.data.push(mappingEmploymentComment);
            }
        },
        addMappingEmploymentAction(mappingEmploymentAction: MappingEmploymentAction) {
            const alreadyExists = this._mappingEmploymentAction.data.some((x: any) => x.id.isEqualTo(mappingEmploymentAction.id) === true);
            if (!alreadyExists) {
                this._mappingEmploymentAction.data.push(mappingEmploymentAction);
            }
        },
        addMappingFactorValue(mappingFactorValue: MappingFactorValue) {
            const alreadyExists = this._mappingFactorValue.data.some(((x: any) => x.id?.isEqualTo(mappingFactorValue.id) === true));
            const factorExistInMapping = this._mappingFactor.data.some((x => x.id != null && x.id?.isEqualTo(mappingFactorValue.mappingFactorID.id) === true));

            if (!alreadyExists && factorExistInMapping) {
                this._mappingFactorValue.data.push(mappingFactorValue);
            }
        }
    }
});
